if (!customElements.get('th-sticky-atc')) {
  class ThStickyAtc extends HTMLElement {
    constructor() {
      super();

      this.RECHARGE_SELECT_SELECTOR = "[data-th-recharge-select]";
      this.RECHARGE_RADIO_INPUT_SELECTOR = ".rc-radio__input";

      this.combinedListingSelect = this.querySelector("[data-combined-listing-select]");
      this.addToCartButton = this.querySelector("#AddToCartSticky");
      this.addToCartPrice = this.querySelector("[data-atc-price]");
      this.buyOnceOption = this.querySelector('[data-sticky-buy-once]');
      this.buyOncePrice = this.buyOnceOption ? this.buyOnceOption.getAttribute("data-sticky-buy-once") : null;
      this.isFeaturedProduct = this.hasAttribute('data-is-featured-product');
      
      // Scroll trigger thresholds as percentages of viewport height
      this.DESKTOP_SCROLL_THRESHOLD = 3; // 30% of viewport height
      this.MOBILE_SCROLL_THRESHOLD = 3.5; // 40% of viewport height

      this.isHandlingPrice = false;
    }

    connectedCallback() {
      this.RECHARGE_SELECT = this.querySelector(this.RECHARGE_SELECT_SELECTOR);

      this.attachListeners();

      document.addEventListener("section-content::changed", () => {
        this.toggleStickyATC();
      });

      // If this is a featured product, listen for product selection changes
      if (this.isFeaturedProduct) {
        document.addEventListener('sticky-atc-product-selected', (e) => {
          this.handleProductSelection(e.detail);
        });
      }

      // Set initial price
      this.handleATCPrice();

      // Add change listener to the select
      if (this.combinedListingSelect) {
        this.combinedListingSelect.addEventListener('change', () => this.handleATCPrice());
      }
    }

    attachListeners() {
      window.addEventListener("scroll", (event) => {
        this.toggleStickyATC();
      });
      if (this.RECHARGE_SELECT) {
        window.addEventListener("th:recharge-ready", (event) => {
          this.handleRechargeChange(event);
        });
        window.addEventListener("th:recharge-change", (event) => {
          this.handleRechargeChange(event);
        });
        this.RECHARGE_SELECT.addEventListener("change", () => {
          this.handleSelectChange();
        });
      }

      if (this.addToCartButton) {
        this.addToCartButton.addEventListener("click", (event) => {
          this.handleAddToCartClick(event);
        });
      }
      if (this.combinedListingSelect) {
        this.combinedListingSelect.addEventListener("change", () => {
          this.handleATCPrice();
        });
      }
    }

    handleProductSelection(detail) {
      if (!this.isFeaturedProduct) return;

      // Update the select to match the selected product
      if (this.combinedListingSelect) {
        const selectedProduct = document.querySelector('.td-apps-item.td-active');
        if (!selectedProduct) return;

        const variantId = selectedProduct.dataset.variantId;
        const sellingPlanId = selectedProduct.dataset.sellingPlanId;

        const matchingOption = Array.from(this.combinedListingSelect.options).find(option => 
          option.dataset.variantId === variantId && 
          option.dataset.sellingPlanId === sellingPlanId
        );

        if (matchingOption) {
          this.combinedListingSelect.value = matchingOption.value;
          this.handleATCPrice();
        }
      }
    }

    handleATCPrice() {
      if (!this.combinedListingSelect) return;
      
      // Prevent infinite loops
      if (this.isHandlingPrice) return;

      try {
        this.isHandlingPrice = true;

        const selectedOption = this.combinedListingSelect.options[this.combinedListingSelect.selectedIndex];
        const variantId = selectedOption.dataset.variantId;
        const sellingPlanId = selectedOption.dataset.sellingPlanId;
        const productId = selectedOption.dataset.productId;

        // Dispatch custom event for product selection
        const productSelectedEvent = new CustomEvent('sticky-atc-product-selected', {
          detail: {
            variantId: variantId,
            sellingPlanId: sellingPlanId,
            productId: productId
          },
          bubbles: true
        });
        this.dispatchEvent(productSelectedEvent);

        // Update the price display
        if (this.priceDisplay) {
          this.priceDisplay.textContent = selectedOption.getAttribute('data-subscription-price');
        }

        if (this.combinedListingSelect.value === this.buyOnceOption?.value) {
          // Handle buy once price
          if (this.buyOncePrice && this.addToCartPrice) {
            this.addToCartPrice.innerText = this.buyOncePrice;
          }
        } else {
          // Handle subscription price
          const subscriptionPrice = selectedOption.getAttribute('data-subscription-price');
          if (subscriptionPrice && this.addToCartPrice) {
            this.addToCartPrice.innerText = subscriptionPrice;
          }
        }
      } finally {
        this.isHandlingPrice = false;
      }
    }

    handleAddToCartClick(event) {
      if (!this.combinedListingSelect) return;

      const selectedOption = this.combinedListingSelect.selectedOptions[0];
      
      if (this.combinedListingSelect.value === this.buyOnceOption?.value) {
        // Handle buy once
        event.preventDefault();
        const variantId = this.buyOnceOption.getAttribute("data-variant-id");
        if (variantId) {
          this.addToCart(variantId);
        }
      } else {
        // Handle subscription
        event.preventDefault();
        const variantId = selectedOption.getAttribute('data-variant-id');
        const sellingPlanId = selectedOption.getAttribute('data-selling-plan-id');
        if (variantId) {
          this.addToCart(variantId, sellingPlanId);
        }
      }
    }
    
    addToCart(variantId, sellingPlanId = null) {
      const item = {
        id: variantId,
        quantity: 1
      };

      if (sellingPlanId) {
        item.selling_plan = sellingPlanId;
      }

      // Disable button during submission
      if (this.addToCartButton) {
        this.addToCartButton.setAttribute('aria-disabled', true);
        this.addToCartButton.classList.add('loading');
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item), // Single item format (not items array) for free gift compatibility
      })
      .then(response => response.json())
      .then(data => {
        this.resetButtonState();
        
        // Dispatch cart:item-added event to trigger cart drawer
        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', { product: data });
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:item-added', { 
            bubbles: true, 
            detail: { product: data } 
          }));
        }
      })
      .catch(error => {
        console.error("Error adding to cart:", error);
        this.resetButtonState();
      });
    }

    resetButtonState() {
      if (this.addToCartButton) {
        this.addToCartButton.removeAttribute('aria-disabled');
        this.addToCartButton.classList.remove('loading');
      }
    }

    toggleStickyATC() {
      const footer = document.getElementById('footer');
      const isFooterVisible = this.elementIsVisibleInViewport(footer, true);

      if (this.isFeaturedProduct) {
        // For featured products, use scroll depth as percentage of viewport
        const scrollPosition = window.scrollY;
        const viewportHeight = window.innerHeight;
        const isMobile = viewportHeight <= 768; // Common mobile breakpoint
        
        const threshold = isMobile ? this.MOBILE_SCROLL_THRESHOLD : this.DESKTOP_SCROLL_THRESHOLD;
        const shouldShow = scrollPosition > (viewportHeight * threshold);

        if (shouldShow && !isFooterVisible) {
          this.classList.add('sticky--visible');
          this.moveAccessiblyIcon(true);
          this.moveMessengerIcon(true);
        } else {
          this.classList.remove('sticky--visible');
          this.moveAccessiblyIcon(false);
          this.moveMessengerIcon(false);
        }
      } else {
        // Original behavior for non-featured products
        const form = document.getElementById(`product-form-${this.dataset.section}`);
        if (!this.elementIsVisibleInViewport(form) && !isFooterVisible) {
          this.classList.add('sticky--visible');
          this.moveAccessiblyIcon(true);
          this.moveMessengerIcon(true);
        } else {
          this.classList.remove('sticky--visible');
          this.moveAccessiblyIcon(false);
          this.moveMessengerIcon(false);
        }
      }
    }

    elementIsVisibleInViewport(el, partiallyVisible = false) {
      const { top } = el.getBoundingClientRect();
      const { innerHeight } = window;
      return partiallyVisible ? top <= (innerHeight / 2) : top >= 76;
    };

    moveAccessiblyIcon(visible) {
      const icon = document.getElementById('accessiblyAppWidgetButton');
      if (icon && visible) {
        icon.classList.add('th-sticky-atc-open');
      } else if (icon && !visible) {
        icon.classList.remove('th-sticky-atc-open');
      }
    }

    moveMessengerIcon(visible) {
      const icons = document.querySelectorAll('#gorgias-chat-container iframe');
      icons.forEach(icon => {

        if (visible) {
          icon.classList.add('th-sticky-atc-open__messenger');
        } else {
          icon.classList.remove('th-sticky-atc-open__messenger');
        }
      });
    }

    // Below is legacy code used for Recharge. Not deleting yet in case they change their minds, but can likely be deleted in future - 2025-02-25

    handleRechargeChange(event) {
      if (!event.detail) {return}
      if (event.detail.value === 'subscription') {
        this.RECHARGE_SELECT.value = 'Monthly';
      } else if (event.detail.value === 'onetime') {
        this.RECHARGE_SELECT.value = 'One-Time';
      }
    }

    handleSelectChange() {
      if (this.RECHARGE_SELECT.value === 'Monthly') {
        document.querySelectorAll(this.RECHARGE_RADIO_INPUT_SELECTOR).forEach((input) => {
          if (input.value === 'subscription') {
            input.parentElement.click();
          }
        });
      } else if (this.RECHARGE_SELECT.value === 'One-Time') {
        document.querySelectorAll(this.RECHARGE_RADIO_INPUT_SELECTOR).forEach((input) => {
          if (input.value === 'onetime') {
            input.parentElement.click();
          }
        });
      }
    }

    handleProductClick(clickedItem) {
      // Remove active class from all items
      this.productItems.forEach(item => {
        item.classList.remove('td-active');
      });

      // Add active class to clicked item
      clickedItem.classList.add('td-active');

      // Store the active product
      this.activeProduct = clickedItem;

      // Update both forms with the selected product's variant ID
      if (this.subscriptionForm) {
        const variantId = clickedItem.dataset.variantId;
        const variantInput = this.subscriptionForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
        }
      }
      if (this.onetimeForm) {
        const variantId = clickedItem.dataset.variantId;
        const variantInput = this.onetimeForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
        }
      }

      // Update prices
      this.updatePrices(clickedItem);

      // Dispatch custom event for product selection
      const productSelectedEvent = new CustomEvent('sticky-atc-product-selected', {
        detail: {
          variantId: clickedItem.dataset.variantId,
          sellingPlanId: clickedItem.dataset.sellingPlanId,
          productId: clickedItem.dataset.productId
        },
        bubbles: true
      });
      this.dispatchEvent(productSelectedEvent);
    }
  }

  customElements.define('th-sticky-atc', ThStickyAtc);
}
